import React, { createContext, useContext, useState, useCallback } from 'react';

interface NavigationPath {
  id: string;
  title: string;
  description: string;
  slides: string[];
}

interface NavigationState {
  currentPath: string;
  currentSlide: number;
  visitedSlides: Set<string>;
  completedPaths: Set<string>;
}

interface NavigationContextType {
  state: NavigationState;
  availablePaths: NavigationPath[];
  selectPath: (pathId: string) => void;
  nextSlide: () => void;
  previousSlide: () => void;
  jumpToSlide: (slideIndex: number) => void;
  isPathCompleted: (pathId: string) => boolean;
  currentProgress: number;
}

const NAVIGATION_PATHS: NavigationPath[] = [
  {
    id: 'wellness-ai',
    title: 'Wellness AI Portal',
    description: 'Experience personalized AI support for clinician well-being',
    slides: ['welcome', 'ai-companion', 'stress-detection', 'meditation', 'progress']
  },
  {
    id: 'core-mind',
    title: 'Core Mind Portal',
    description: 'Explore advanced system management and oversight capabilities',
    slides: ['welcome', 'dashboard', 'resource-allocation', 'analytics', 'configuration']
  },
  {
    id: 'neural-lab',
    title: 'Neural Lab',
    description: 'Discover cutting-edge research and analytics tools',
    slides: ['welcome', 'pattern-recognition', 'predictive-modeling', 'research-tools', 'collaboration']
  }
];

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<NavigationState>({
    currentPath: '',
    currentSlide: 0,
    visitedSlides: new Set(),
    completedPaths: new Set()
  });

  const selectPath = useCallback((pathId: string) => {
    setState(prev => ({
      ...prev,
      currentPath: pathId,
      currentSlide: 0
    }));
  }, []);

  const nextSlide = useCallback(() => {
    setState(prev => {
      const currentPath = NAVIGATION_PATHS.find(p => p.id === prev.currentPath);
      if (!currentPath) return prev;

      const newSlideIndex = prev.currentSlide + 1;
      const currentSlideId = `${prev.currentPath}-${currentPath.slides[prev.currentSlide]}`;
      const newVisitedSlides = new Set(prev.visitedSlides).add(currentSlideId);

      // Check if path is completed
      let newCompletedPaths = prev.completedPaths;
      if (newSlideIndex >= currentPath.slides.length) {
        newCompletedPaths = new Set(prev.completedPaths).add(prev.currentPath);
      }

      return {
        ...prev,
        currentSlide: Math.min(newSlideIndex, currentPath.slides.length - 1),
        visitedSlides: newVisitedSlides,
        completedPaths: newCompletedPaths
      };
    });
  }, []);

  const previousSlide = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentSlide: Math.max(0, prev.currentSlide - 1)
    }));
  }, []);

  const jumpToSlide = useCallback((slideIndex: number) => {
    setState(prev => {
      const currentPath = NAVIGATION_PATHS.find(p => p.id === prev.currentPath);
      if (!currentPath) return prev;

      return {
        ...prev,
        currentSlide: Math.max(0, Math.min(slideIndex, currentPath.slides.length - 1))
      };
    });
  }, []);

  const isPathCompleted = useCallback((pathId: string) => {
    return state.completedPaths.has(pathId);
  }, [state.completedPaths]);

  const currentProgress = useCallback(() => {
    const currentPath = NAVIGATION_PATHS.find(p => p.id === state.currentPath);
    if (!currentPath) return 0;

    return (state.currentSlide + 1) / currentPath.slides.length * 100;
  }, [state.currentPath, state.currentSlide])();

  const value = {
    state,
    availablePaths: NAVIGATION_PATHS,
    selectPath,
    nextSlide,
    previousSlide,
    jumpToSlide,
    isPathCompleted,
    currentProgress
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
